import React from "react"
import { ModificationOrderLines } from "../../../classes/order/ModificationOrderLines"
import { ModificationAddresses } from "../../../classes/order/OrderModificationAddresses"
import { Deliveries } from "../../../classes/order/Deliveries"
import { Refunds } from "../../../classes/order/Refunds"
import { Compensations } from "../../../classes/order/Compensations"
import { Cancellations } from "../../../classes/order/Cancellations"
import { Releases } from "../../../classes/order/Releases"
import { Order } from "lib/types/generated/graphql-types"
import { NoticeBanner } from "../../Ui/Messages/NoticeBanner"
import { NoticeWrapper } from "../../../views/Orders/OrderViews/ViewOrder.styled"
import { useNavigate } from "react-router-dom"
import { ReactComponent as SettingsIcon } from "images/icons/gear.svg"
import { WithAuth } from "../../Auth"
import { PermissionGroups } from "../../../routes/constants"

type Props = {
  order: Order
}

const Notice = ({ order }: Props) => {
  const navigate = useNavigate()

  const modificationOrderLines = new ModificationOrderLines(order?.Modifications)
  const modificationsAddresses = new ModificationAddresses(order?.Modifications)
  const deliveries = new Deliveries(order?.Deliveries)
  const refunds = new Refunds(order?.Refunds)
  const compensations = new Compensations(order?.Compensations)
  const cancellations = new Cancellations(order?.Cancellations)
  const releases = new Releases(order?.Releases)

  enum OPERATION_TYPE {
    DELIVERY = "Delivery",
    REFUND = "Refund",
    RELEASE = "Release",
    COMPENSATION = "Compensation",
    CANCELLATION = "Cancellation",
    MODIFICATION_ORDER_LINES = "Modification order lines",
    MODIFICATION_ADDRESSES = "Modification address"
  }

  const getOngoingNotice = (type: OPERATION_TYPE) => {
    return (
      <NoticeBanner type="reminder">
        <NoticeWrapper>
          Ongoing {type}{" "}
          <WithAuth requiredGroups={PermissionGroups.orders.write}>
            <span onClick={() => navigate(`/orders/manage-order/${order?.id}`)}>
              Actions <SettingsIcon />
            </span>
          </WithAuth>
        </NoticeWrapper>
      </NoticeBanner>
    )
  }

  const getFailedNotice = (type: OPERATION_TYPE) => {
    return (
      <NoticeBanner type="important">
        <NoticeWrapper>
          Failed {type}{" "}
          <WithAuth requiredGroups={PermissionGroups.orders.write}>
            <span onClick={() => navigate(`/orders/manage-order/${order?.id}`)}>
              Actions <SettingsIcon />
            </span>
          </WithAuth>
        </NoticeWrapper>
      </NoticeBanner>
    )
  }

  const getNotCompletedNotice = (type: OPERATION_TYPE) => {
    if (type === OPERATION_TYPE.MODIFICATION_ADDRESSES)
      return (
        <NoticeBanner type="reminder">
          <NoticeWrapper>Not completed address update</NoticeWrapper>
        </NoticeBanner>
      )

    return (
      <NoticeBanner type="reminder">
        <NoticeWrapper>
          Not completed {type}
          <WithAuth requiredGroups={PermissionGroups.orders.write}>
            <span onClick={() => navigate(`/orders/manage-order/${order?.id}`)}>
              Actions <SettingsIcon />
            </span>
          </WithAuth>
        </NoticeWrapper>
      </NoticeBanner>
    )
  }

  return (
    <div>
      {refunds.pending() ? getNotCompletedNotice(OPERATION_TYPE.REFUND) : <></>}
      {deliveries.pending() ? getNotCompletedNotice(OPERATION_TYPE.DELIVERY) : <></>}
      {compensations.pending() ? getNotCompletedNotice(OPERATION_TYPE.COMPENSATION) : <></>}
      {cancellations.pending() ? getNotCompletedNotice(OPERATION_TYPE.CANCELLATION) : <></>}
      {modificationOrderLines.pending() ? (
        getNotCompletedNotice(OPERATION_TYPE.MODIFICATION_ORDER_LINES)
      ) : (
        <></>
      )}
      {modificationsAddresses.pending() ? (
        getNotCompletedNotice(OPERATION_TYPE.MODIFICATION_ADDRESSES)
      ) : (
        <></>
      )}
      {releases.pending() ? getNotCompletedNotice(OPERATION_TYPE.RELEASE) : <></>}

      {refunds.ongoing() ? getOngoingNotice(OPERATION_TYPE.REFUND) : <></>}
      {deliveries.ongoing() ? getOngoingNotice(OPERATION_TYPE.DELIVERY) : <></>}
      {compensations.ongoing() ? getOngoingNotice(OPERATION_TYPE.COMPENSATION) : <></>}
      {cancellations.ongoing() ? getOngoingNotice(OPERATION_TYPE.CANCELLATION) : <></>}
      {modificationOrderLines.ongoing() ? (
        getOngoingNotice(OPERATION_TYPE.MODIFICATION_ORDER_LINES)
      ) : (
        <></>
      )}
      {releases.ongoing() ? getOngoingNotice(OPERATION_TYPE.RELEASE) : <></>}

      {refunds.failed() ? getFailedNotice(OPERATION_TYPE.REFUND) : <></>}
      {deliveries.failed() ? getFailedNotice(OPERATION_TYPE.DELIVERY) : <></>}
      {compensations.failed() ? getFailedNotice(OPERATION_TYPE.COMPENSATION) : <></>}
      {cancellations.failed() ? getFailedNotice(OPERATION_TYPE.CANCELLATION) : <></>}
      {modificationOrderLines.failed() ? (
        getFailedNotice(OPERATION_TYPE.MODIFICATION_ORDER_LINES)
      ) : (
        <></>
      )}
      {releases.failed() ? getFailedNotice(OPERATION_TYPE.RELEASE) : <></>}
    </div>
  )
}

export default Notice
